import { Coding } from "@medplum/fhirtypes";
import { LabRowData } from "../../tables/lab/lab-rows";
import { ProcedureRowData } from "../../tables/procedure/procedure-rows";
import { ObservationSeries, ProcedureSeries } from "../../table-transforms/series-utils";
import { TableRow } from "../../utils/tables-metadata";
import { FeaturedCategory } from "../categories";
import {
  BREAST_CANCER_SCREENING_CPT_CODES,
  BREAST_CANCER_SCREENING_LOINC_CODES,
  BREAST_CANCER_SCREENING_SNOMED_CODES,
} from "../reference-data/breast-cancer";
import {
  COLORECTAL_CANCER_SCREENING_CPT_CODES,
  COLORECTAL_CANCER_SCREENING_LOINC_CODES,
  COLORECTAL_CANCER_SCREENING_SNOMED_CODES,
} from "../reference-data/colorectal-cancer";
import {
  HBA1C_CONTROL_CPT_CODES,
  HBA1C_CONTROL_LOINC_CODES,
  HBA1C_CONTROL_SNOMED_CODES,
} from "../reference-data/hba1c-control";
import {
  OSTEOPOROSIS_SCREENING_CPT_CODES,
  OSTEOPOROSIS_SCREENING_LOINC_CODES,
  OSTEOPOROSIS_SCREENING_SNOMED_CODES,
} from "../reference-data/osteoporosis";

/**
 * SNOMED-CT and CPT codes for breast cancer procedures
 */
const BREAST_CANCER_SCREENING_PROCEDURE_CODES = [
  ...BREAST_CANCER_SCREENING_SNOMED_CODES,
  ...BREAST_CANCER_SCREENING_CPT_CODES,
];

/**
 * SNOMED-CT and CPT codes for colorectal cancer procedures
 */
const COLORECTAL_CANCER_SCREENING_PROCEDURE_CODES = [
  ...COLORECTAL_CANCER_SCREENING_SNOMED_CODES,
  ...COLORECTAL_CANCER_SCREENING_CPT_CODES,
];

/**
 * SNOMED-CT and CPT codes for osteoporosis (bone density) procedures
 */
const OSTEOPOROSIS_SCREENING_PROCEDURE_CODES = [
  ...OSTEOPOROSIS_SCREENING_SNOMED_CODES,
  ...OSTEOPOROSIS_SCREENING_CPT_CODES,
];

/**
 * SNOMED-CT and CPT codes for HbA1c control procedures
 */
const HBA1C_CONTROL_PROCEDURE_CODES = [...HBA1C_CONTROL_SNOMED_CODES, ...HBA1C_CONTROL_CPT_CODES];

/**
 * Whether an observation row matches any LOINC or SNOMED code (series.code is system-agnostic string).
 */
function labRowMatchesLoincOrSnomed(
  obs: LabRowData,
  loincCodes: readonly string[],
  snomedCodes: readonly string[]
): boolean {
  const series = obs.originalData as ObservationSeries;
  const code = series.code;
  return (
    loincCodes.some(loinc => code.includes(loinc)) || snomedCodes.some(sct => code.includes(sct))
  );
}

/**
 * Helper to get all codings from a procedure originalData (handles both single and series)
 */
function getAllProcedureCodings(series: ProcedureSeries): Coding[] {
  return series.resources.flatMap(procedure => procedure.code?.coding ?? []);
}

/**
 * Filter breast cancer observations (LOINC or SNOMED on observation) for Featured section
 */
export function getBreastCancerPreventativeObservations(observations: LabRowData[]): LabRowData[] {
  return observations.filter(obs =>
    labRowMatchesLoincOrSnomed(
      obs,
      BREAST_CANCER_SCREENING_LOINC_CODES,
      BREAST_CANCER_SCREENING_SNOMED_CODES
    )
  );
}

/**
 * Filter breast cancer procedures (SNOMED or CPT) for Featured section
 */
export function getBreastCancerPreventativeProcedures(
  procedures: ProcedureRowData[]
): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = getAllProcedureCodings(procedure.originalData);
    return codings.some(c => c.code && BREAST_CANCER_SCREENING_PROCEDURE_CODES.includes(c.code));
  });
}

/**
 * Filter colorectal cancer observations (LOINC or SNOMED) for Featured section
 */
export function getColorectalCancerPreventativeObservations(
  observations: LabRowData[]
): LabRowData[] {
  return observations.filter(obs =>
    labRowMatchesLoincOrSnomed(
      obs,
      COLORECTAL_CANCER_SCREENING_LOINC_CODES,
      COLORECTAL_CANCER_SCREENING_SNOMED_CODES
    )
  );
}

/**
 * Filter colorectal cancer procedures (SNOMED or CPT) for Featured section
 */
export function getColorectalCancerPreventativeProcedures(
  procedures: ProcedureRowData[]
): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = getAllProcedureCodings(procedure.originalData);
    return codings.some(
      c => c.code && COLORECTAL_CANCER_SCREENING_PROCEDURE_CODES.includes(c.code)
    );
  });
}

/**
 * Filter osteoporosis observations (LOINC or SNOMED) for Featured section
 */
export function getOsteoporosisPreventativeObservations(observations: LabRowData[]): LabRowData[] {
  return observations.filter(obs =>
    labRowMatchesLoincOrSnomed(
      obs,
      OSTEOPOROSIS_SCREENING_LOINC_CODES,
      OSTEOPOROSIS_SCREENING_SNOMED_CODES
    )
  );
}

/**
 * Filter osteoporosis procedures (SNOMED or CPT) for Featured section
 */
export function getOsteoporosisPreventativeProcedures(
  procedures: ProcedureRowData[]
): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = getAllProcedureCodings(procedure.originalData);
    return codings.some(c => c.code && OSTEOPOROSIS_SCREENING_PROCEDURE_CODES.includes(c.code));
  });
}

/**
 * Filter HbA1c control observations (LOINC or SNOMED) for Featured section
 */
export function getHba1cControlPreventativeObservations(observations: LabRowData[]): LabRowData[] {
  return observations.filter(obs =>
    labRowMatchesLoincOrSnomed(obs, HBA1C_CONTROL_LOINC_CODES, HBA1C_CONTROL_SNOMED_CODES)
  );
}

/**
 * Filter HbA1c control procedures (SNOMED or CPT) for Featured section
 */
export function getHba1cControlPreventativeProcedures(
  procedures: ProcedureRowData[]
): ProcedureRowData[] {
  return procedures.filter(procedure => {
    const codings = getAllProcedureCodings(procedure.originalData);
    return codings.some(c => c.code && HBA1C_CONTROL_PROCEDURE_CODES.includes(c.code));
  });
}

export function getFeaturedPreventativeScreening(): FeaturedCategory {
  return {
    category: "Preventative Screenings",
    subCategories: [
      {
        subCategory: "Breast Cancer (Labs)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) =>
          getBreastCancerPreventativeObservations(data as LabRowData[]),
      },
      {
        subCategory: "Breast Cancer (Procedures)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) =>
          getBreastCancerPreventativeProcedures(data as ProcedureRowData[]),
      },
      {
        subCategory: "Colorectal Cancer (Labs)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) =>
          getColorectalCancerPreventativeObservations(data as LabRowData[]),
      },
      {
        subCategory: "Colorectal Cancer (Procedures)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) =>
          getColorectalCancerPreventativeProcedures(data as ProcedureRowData[]),
      },
      {
        subCategory: "Osteoporosis (Labs)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) =>
          getOsteoporosisPreventativeObservations(data as LabRowData[]),
      },
      {
        subCategory: "Osteoporosis (Procedures)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) =>
          getOsteoporosisPreventativeProcedures(data as ProcedureRowData[]),
      },
      {
        subCategory: "HbA1c Control (Labs)",
        tableType: "LaboratoryObservations",
        getRows: (data: TableRow[]) =>
          getHba1cControlPreventativeObservations(data as LabRowData[]),
      },
      {
        subCategory: "HbA1c Control (Procedures)",
        tableType: "Procedure",
        getRows: (data: TableRow[]) =>
          getHba1cControlPreventativeProcedures(data as ProcedureRowData[]),
      },
    ],
  };
}
